/**
 * AddMembersToQualificationDialog Component
 * Dialog for bulk-assigning team members to a qualification
 */

import { memo, useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { CheckCircle } from 'lucide-react';

const AddMembersToQualificationDialog = memo(({
  open,
  onOpenChange,
  qualification,
  availableMembers,
  onAddMembers
}) => {
  const [selectedMembers, setSelectedMembers] = useState([]);

  // Reset selection when dialog opens/closes or qualification changes
  useEffect(() => {
    if (!open) {
      setSelectedMembers([]);
    }
  }, [open]);

  const toggleMember = (memberId) => {
    setSelectedMembers(prev =>
      prev.includes(memberId)
        ? prev.filter(id => id !== memberId)
        : [...prev, memberId]
    );
  };

  const handleSubmit = () => {
    onAddMembers(selectedMembers);
    setSelectedMembers([]);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Add Team Members to {qualification?.name}</DialogTitle>
          <DialogDescription>
            Select team members who don't currently have this qualification assigned
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto py-4 space-y-2">
          {availableMembers.map(member => {
            const isSelected = selectedMembers.includes(member.id);
            return (
              <div
                key={member.id}
                onClick={() => toggleMember(member.id)}
                className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all ${
                  isSelected ? 'bg-blue-50 border-2 border-blue-500' : 'bg-white border border-gray-200 hover:border-blue-300'
                }`}
              >
                <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                  isSelected ? 'bg-blue-500 border-blue-500' : 'border-gray-300'
                }`}>
                  {isSelected && <CheckCircle className="h-4 w-4 text-white" />}
                </div>
                <div className="w-10 h-10 bg-gradient-to-br from-gray-100 to-gray-200 rounded-xl flex items-center justify-center text-gray-700 text-sm font-medium flex-shrink-0">
                  {member.name.split(' ').map(n => n[0]).join('').toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900">{member.name}</p>
                  <p className="text-xs text-gray-500">Team Member</p>
                </div>
              </div>
            );
          })}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={selectedMembers.length === 0}
          >
            Add {selectedMembers.length > 0 ? `(${selectedMembers.length})` : ''} Team Member{selectedMembers.length !== 1 ? 's' : ''}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
});

AddMembersToQualificationDialog.displayName = 'AddMembersToQualificationDialog';

export default AddMembersToQualificationDialog;
